import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as amplitude from '@amplitude/analytics-browser';

import { DataTable } from '@unified-checkout/design-system';
import type { ITransferListing } from '@app/dashboard/types/innerLayout/balance_and_transfers/transfers/table-listing';

import type { IActiveTab } from '@app/dashboard/types/innerLayout/balance_and_transfers/transfers/table-tabs';
import { getTransfers } from '@app/dashboard/services/inner_layout/balance_and_transfers/transfers';
import { getFormattedDates } from '@app/dashboard/utils/getFormattedDates';
import { useLangStore } from '@app/dashboard/store/lang';
import { useTrackOnce } from '@app/dashboard/hooks/useTrackOnce';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { useCancelQuery } from '@app/dashboard/hooks/useCancelQuery';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import {
  BALANCE_TRANSFERS_EVENTS,
  BALANCE_TRANSFERS_EVENTS_PROPERTIES,
} from '@app/dashboard/constants/tracking_events/balance-and-transfers';

import { useTransfersTableLogic } from '../../../../hooks/useTransfersTableLogic';
import TransferDrawer from '../../../../components/TransferDrawerDetails';
import TableTabs from '../TableTabs';

import styles from './table-listing.module.scss';

const TableListing = ({
  appliedRanges,
  isTransfersLoading,
  onTransferLoadingChange,
  shouldDisableTabs,
  tabsProps,
  activeBalance,
}: ITransferListing) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<IActiveTab | null>(null);

  const {
    accountId,
    haveAccounts,
    shouldDisableQuery: disableQuery,
    transfersQueryKey,
    columns,
    drawerState,
    handleCloseDrawer,
    handleTableRowClick,
    handleResponseChange,
    transfersCount,
    firstMount,
  } = useTransfersTableLogic({
    activeBalance,
  });

  const { lang } = useLangStore();
  const screenWidth = useScreenWidth();

  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const queryFilterKeys = useMemo(
    () => [QUERY_KEYS.GET_TRANSFERS_FILTERS, lang],
    [lang]
  );

  const queryFunctionParams = useMemo(() => {
    if(!appliedRanges.start || !appliedRanges.end) return;
    
    const formattedDateRange = getFormattedDates([
      appliedRanges.start,
      appliedRanges.end,
    ] as [Date, Date]);

    return {
      status: activeTab?.id ?? '',
      start_date: formattedDateRange.start_date,
      end_date: formattedDateRange.end_date,
      ...(haveAccounts === false ? {} : { bank_account_number: accountId }),
    };
  }, [activeTab, appliedRanges, lang, accountId, haveAccounts]);

  const handleTabChange = (tab: IActiveTab) => {
    setActiveTab(tab);
  };

  const shouldDisableQuery =
    disableQuery ||
    !activeTab?.id ||
    !appliedRanges.start ||
    !appliedRanges.end;

  useCancelQuery({
    queryKey: queryFilterKeys,
    deps: [lang, accountId, haveAccounts],
  });

  useTrackOnce({
    trackFn: () => {
      amplitude.track(
        BALANCE_TRANSFERS_EVENTS.TRANSFERS_PAGE.VIEW_TRANSFERS_TABLE,
        {
          [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.ACCOUNT_ID]: accountId,
          [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.CURRENCY]:
            activeBalance?.currency,
          [BALANCE_TRANSFERS_EVENTS_PROPERTIES.TRANSFERS_TABLE
            .TRANSFER_COUNT_VISIBLE]: transfersCount || 0,
        }
      );
    },
    shouldTrack: !!activeBalance && !isTransfersLoading && !firstMount.current,
    deps: [activeBalance, accountId, isTransfersLoading],
  });

  return (
    <div className={styles['table-listing']}>
      <TableTabs
        activeTab={activeTab}
        handleTabChange={handleTabChange}
        disableTabs={isTransfersLoading || shouldDisableTabs}
        tabsProps={tabsProps}
      />

      <TransferDrawer
        drawerState={drawerState}
        handleCloseDrawer={handleCloseDrawer}
      />

      <DataTable
        lang={lang}
        tableBody={{
          lang: lang,
          dataLocation: 'data.data.results',
          columnsMap: columns,
          rowClickHandler: handleTableRowClick,
        }}
        pagination={{
          hideLabels: isMobile,
          type: 'page_based',
          dataLocation: {
            totalItems: 'data.data.count',
          },
          isMobile: isMobile,
        }}
        queries={{
          queryKeys: transfersQueryKey,
          queryFunction: getTransfers,
          queryFunctionParams,
          onLoadingChange: onTransferLoadingChange,
          onResponseChange: handleResponseChange,
          enabledQuery: !shouldDisableQuery,
        }}
        emptyState={{
          text: t('innerLayout.balanceTransfers.table.emptyState.noTransfers'),
          description: t('innerLayout.balanceTransfers.table.emptyState.hint'),
        }}
      />
    </div>
  );
};

export default TableListing;
